import { PURPOSE_NAMES } from '@nw/shared';
import type { ChannelLimits, PersonLabel } from '@nw/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '../../lib/api';
import { useRealtime } from '../../lib/realtime';

/** Shapes of the lane B2a admin API (docs/modules/whatsapp.md). */

export type ChannelKind = 'whatsapp' | 'telegram';

export type ChannelStatus = 'created' | 'qr_required' | 'connecting' | 'ready' | 'disconnected' | 'restricted' | 'failed';

export interface ChannelDto {
  id: string;
  name: string;
  phone: string | null;
  pushName: string | null;
  provider: 'openwa' | 'fake' | 'telegram_bot';
  /** D-140: a WhatsApp number or a Telegram bot */
  kind: ChannelKind;
  /** Telegram bot username (t.me links); null for WhatsApp */
  handle: string | null;
  providerSessionId: string;
  roles: string[];
  status: ChannelStatus;
  statusChangedAt: string;
  providerStatus: string | null;
  lastError: string | null;
  restriction: unknown;
  killSwitch: boolean;
  limits: ChannelLimits;
  connectedAt: string | null;
  lastSeenAt: string | null;
  lastEventAt: string | null;
  createdAt: string;
  webhook: { path: string; secretConfigured: boolean };
  quota: { sentToday: number; dailyCap: number; remaining: number; resetsAt: string };
  queue: number;
  groupsCount: number;
}

export interface ChannelEvent {
  id: string;
  type: string;
  payload: Record<string, unknown> | null;
  at: string;
}

export type ChannelDetail = ChannelDto & { events: ChannelEvent[] };

export interface QrResponse {
  status: ChannelStatus;
  qr: string | null;
  expiresInSec?: number | null;
}

export interface Restrictions {
  status: ChannelStatus;
  active: boolean;
  current: unknown;
  checkedAt: string | null;
  lastRestrictionAt: string | null;
  history: { id: string; at: string; payload: Record<string, unknown> | null }[];
  failedToday: number;
  attemptedToday: number;
  disconnects24h: { count: number; last: string | null };
}

export interface SettingsSummary {
  provider: 'openwa' | 'fake';
  openwa: {
    baseUrl: string | null;
    apiKeyConfigured: boolean;
    reachable: boolean;
    latencyMs: number | null;
    engine?: string | null;
    version?: string | null;
    error?: string | null;
  };
  session: { name: string; providerStatus: string | null; status: ChannelStatus; lastSeenAt: string | null };
  webhook: { path: string; secretConfigured: boolean; lastEventAt: string | null; events: string[]; rejected24h: number };
  notExposed: string[];
}

export interface AuditEntry {
  id: string;
  action: string;
  userId: string | null;
  diff: unknown;
  at: string;
}

export interface Profile {
  channelId: string;
  phone: string | null;
  editable: boolean;
  pushName: string | null;
  about: string | null;
  aboutSource: 'live' | 'last_saved' | 'unknown';
  picture: { set: boolean | null; url: string | null };
  lastSaved: unknown;
  liveError: string | null;
  recentChanges: AuditEntry[];
}

export interface GroupRoute {
  purpose: string;
  clientId: string;
  siteId: string | null;
  enabled: boolean;
}

export interface GroupDto {
  id: string;
  channelId: string;
  waChatId: string;
  name: string;
  description: string | null;
  pictureUrl: string | null;
  participantsCount: number;
  isMember: boolean;
  isAdmin: boolean;
  isTestGroup: boolean;
  clientId: string | null;
  clientName: string | null;
  siteId: string | null;
  siteName: string | null;
  routes: GroupRoute[];
  lastMessageAt: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  /** D-140: the kind of the channel the group belongs to */
  channelKind: ChannelKind;
  /** only with ?members=1: up to 8 stored members (not us, not left), admins -> named -> phone -> hidden */
  members?: { label: PersonLabel; isAdmin: boolean }[];
  /** only with ?members=1: active members whose phone WhatsApp hides */
  membersHidden?: number;
}

export interface GroupList {
  items: GroupDto[];
  counts: { all: number; assigned: number; unassigned: number; test: number };
  lastSyncedAt: string | null;
}

export interface Participant {
  waId: string;
  /** the member's contact (null = us or unknown) */
  contactId: string | null;
  /** always false (left members are not listed) */
  left: boolean;
  /** digits; null = hidden by WhatsApp (@lid without a known number) */
  phone: string | null;
  name: string | null;
  label: PersonLabel;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isMe: boolean;
}

export type GroupDetail = GroupDto & {
  channel: { id: string; name: string; status: ChannelStatus; phone: string | null; kind: ChannelKind };
  /** the members were read from WhatsApp in this request (false = the stored list) */
  live: boolean;
  liveError: string | null;
  /** stored members (also when the number is offline); null only when the group was never synced */
  participants: Participant[] | null;
  /** last time the member list was stored from WhatsApp */
  participantsSyncedAt: string | null;
  inviteLink: string | null;
  /** current switches read from WhatsApp; null when the engine can't read them back */
  settings: { announce: boolean | null; restrict: boolean | null } | null;
  recentChanges: AuditEntry[];
};

export interface InvitePreview {
  code: string;
  channelId: string;
  waChatId: string | null;
  name: string;
  description: string | null;
  participantsCount: number | null;
  pictureUrl: string | null;
  alreadyMember: boolean;
  existingGroupId: string | null;
}

export type JoinResult =
  | { status: 'joined'; waChatId: string; group: GroupDto }
  | { status: 'pending_approval'; waChatId: string; group: null };

export interface JoinRequest {
  waId: string;
  phone: string | null;
  name: string | null;
  requestedAt: string | null;
}

export interface ContactDto {
  id: string;
  channelId: string;
  waId: string;
  phone: string | null;
  name: string | null;
  pushName: string | null;
  /** name saved in the connected phone's address book */
  deviceName: string | null;
  /** WhatsApp Business verified name */
  verifiedName: string | null;
  /** saved in the connected phone's address book */
  isMyContact: boolean;
  /** last address-book sync that saw this entry */
  deviceSyncedAt: string | null;
  /** active group memberships, by name */
  groups: { id: string; name: string }[];
  displayName: string | null;
  label: PersonLabel;
  isBlocked: boolean;
  blockNote: string | null;
  blockedAt: string | null;
  hasInbound: boolean;
  createdFrom: 'manual' | 'inbound' | 'group' | 'import' | 'addressbook';
  clientId: string | null;
  clientName: string | null;
  role: string | null;
  isTeam: boolean;
  language: 'he' | 'en' | null;
  notes: string | null;
  lastMessageAt: string | null;
  conversationId: string | null;
  createdAt: string;
}

export interface ContactList {
  items: ContactDto[];
  nextCursor: string | null;
  total: number;
  /** the number's last address-book import */
  addressBook: { syncedAt: string | null };
}

/** Lane B4 (clients) — only the fields this lane reads. */
export interface ClientOption {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'paused';
  sites: { id: string; displayName: string; status: string }[];
  group: { id: string; name: string } | null;
}

// ---------------- query keys ----------------
export const qk = {
  channels: ['wa', 'channels'] as const,
  channel: (id: string) => ['wa', 'channel', id] as const,
  qr: (id: string) => ['wa', 'channel', id, 'qr'] as const,
  restrictions: (id: string) => ['wa', 'channel', id, 'restrictions'] as const,
  settings: (id: string) => ['wa', 'channel', id, 'settings'] as const,
  profile: (id: string) => ['wa', 'channel', id, 'profile'] as const,
  groups: ['wa', 'groups'] as const,
  /** the groups list with a members preview (?members=1); under qk.groups so every groups invalidation refreshes it */
  groupsMembers: ['wa', 'groups', 'members'] as const,
  group: (id: string) => ['wa', 'group', id] as const,
  joinRequests: (id: string) => ['wa', 'group', id, 'join-requests'] as const,
  contacts: ['wa', 'contacts'] as const,
  clients: ['wa', 'client-options'] as const,
};

// ---------------- hooks ----------------
export function useChannels() {
  return useQuery({ queryKey: qk.channels, queryFn: () => api<{ items: ChannelDto[] }>('/api/channels').then((r) => r.items) });
}

/** The number the UI shows: the "client alerts" number, else the first WhatsApp one. A Telegram bot is never it (D-140). */
export function pickMainChannel(items: ChannelDto[] | undefined): ChannelDto | undefined {
  const wa = (items ?? []).filter((c) => c.kind === 'whatsapp');
  return wa.find((c) => c.roles.includes('client_alerts')) ?? wa[0];
}

/** D-150: the WhatsApp numbers only (a Telegram bot is not a number), in the server's order. */
export function waChannels(items: ChannelDto[] | undefined): ChannelDto[] {
  return (items ?? []).filter((c) => c.kind === 'whatsapp');
}

/** D-150: the number chosen by `?channel=` when it is a WhatsApp number, else the main one (unknown id / a Telegram bot). */
export function pickChannel(items: ChannelDto[] | undefined, id?: string): ChannelDto | undefined {
  return (id ? waChannels(items).find((c) => c.id === id) : undefined) ?? pickMainChannel(items);
}

/** D-150: every WhatsApp number's id, for actions that run on all of them ("רענון הרשימה" without a number filter). */
export function useWaChannelIds(): string[] {
  const channels = useChannels();
  return waChannels(channels.data).map((c) => c.id);
}

export function useChannel(id: string | undefined) {
  return useQuery({
    queryKey: qk.channel(id ?? '-'),
    queryFn: () => api<ChannelDetail>(`/api/channels/${id}`),
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });
}

/** Keeps channel data fresh when the server pushes a status change. */
export function useChannelStatusLive() {
  const qc = useQueryClient();
  const onStatus = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['wa', 'channel'] });
    void qc.invalidateQueries({ queryKey: qk.channels });
  }, [qc]);
  useRealtime('channel.status', onStatus);
}

export function useSettingsSummary(channelId: string | undefined) {
  return useQuery({
    queryKey: qk.settings(channelId ?? '-'),
    queryFn: () => api<SettingsSummary>(`/api/channels/${channelId}/settings-summary`),
    enabled: Boolean(channelId),
    staleTime: 30_000,
  });
}

/** All groups with up to 8 stored members each (GET /api/groups?members=1, D-125); D-150: of one number when `channelId` is given. */
export function useGroupsWithMembers(enabled = true, channelId?: string) {
  return useQuery({
    queryKey: channelId ? [...qk.groupsMembers, channelId] : qk.groupsMembers,
    queryFn: () => api<GroupList>('/api/groups', { query: { filter: 'all', members: '1', channelId } }),
    enabled,
  });
}

export function useClientOptions(enabled = true) {
  return useQuery({
    queryKey: qk.clients,
    queryFn: () => api<{ items: ClientOption[] }>('/api/clients', { query: { limit: 200 } }).then((r) => r.items),
    enabled,
    staleTime: 60_000,
  });
}

// ---------------- labels ----------------
export const ROUTE_PURPOSE: Record<string, [string, string]> = {
  alerts: ['התראות', 'התראות'],
  daily_summary: [PURPOSE_NAMES.daily_summary, 'יומי'],
  weekly_summary: [PURPOSE_NAMES.weekly_summary, 'שבועי'],
  general: ['כללי', 'כללי'],
  test: ['בדיקה', 'בדיקה'],
};

export function purposesOf(g: GroupDto): string[] {
  const out: string[] = [];
  for (const r of g.routes) if (r.enabled && !out.includes(r.purpose)) out.push(r.purpose);
  return out;
}
